import type { ConsentCategory, ConsentState, CookieInventoryItem } from './types';

export const CONSENT_KEY = 'markflow-consent-v1';
export const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;
export const CONSENT_SCHEMA_VERSION = 1 as const;

export const DEFAULT_CATEGORIES: Record<ConsentCategory, 'granted' | 'denied'> = {
  necessary: 'granted',
  functional: 'denied',
  analytics: 'denied',
  marketing: 'denied',
};

export const COOKIE_INVENTORY: Record<ConsentCategory, CookieInventoryItem[]> = {
  necessary: [
    { name: 'connect.sid', purpose: 'Authentication session', duration: '30 days', type: 'cookie' },
    { name: CONSENT_KEY, purpose: 'Cookie preferences', duration: '180 days', type: 'localStorage' },
    {
      name: 'fonts.googleapis.com / fonts.gstatic.com',
      purpose: 'Web font delivery (Inter) — Google LLC',
      duration: 'Per request',
      type: 'request',
    },
    {
      name: 'icons.duckduckgo.com',
      purpose: 'Bookmark icon fallback (3rd-party request, DuckDuckGo — no tracking)',
      duration: 'Per request',
      type: 'request',
    },
  ],
  functional: [
    { name: 'markflow-lang', purpose: 'UI language', duration: 'Until cleared', type: 'localStorage' },
    { name: 'theme', purpose: 'Light/dark theme', duration: 'Until cleared', type: 'localStorage' },
    { name: 'markflow-view-prefs', purpose: 'Grid/list/headlines view preference', duration: 'Until cleared', type: 'localStorage' },
    { name: 'markflow-sidebar-prefs', purpose: 'Sidebar folders/tags collapse state', duration: 'Until cleared', type: 'localStorage' },
    { name: 'google.com/s2/favicons', purpose: 'Bookmark icons (3rd-party request)', duration: '—', type: 'request' },
  ],
  analytics: [
    {
      name: 'stats.kovalnya.com',
      purpose: 'Privacy-friendly, cookieless analytics (self-hosted Umami) — page views & funnel events. No cookies, no cross-site tracking.',
      duration: 'Per request',
      type: 'request',
    },
  ],
  marketing: [],
};

export function isExpired(state: ConsentState): boolean {
  return Date.now() - state.timestamp > CONSENT_MAX_AGE_MS;
}

export function readConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isValidState(parsed)) return null;
    if (parsed.version !== CONSENT_SCHEMA_VERSION) return null;
    if (parsed.categories.necessary !== 'granted') return null;
    if (isExpired(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeConsent(state: ConsentState): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable (private mode quota, disabled) — no-op
  }
}

export function clearConsent(): void {
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    // no-op
  }
}

function isValidState(value: unknown): value is ConsentState {
  if (typeof value !== 'object' || value === null) return false;
  const s = value as Record<string, unknown>;
  if (typeof s.version !== 'number') return false;
  if (typeof s.timestamp !== 'number') return false;
  if (typeof s.locale !== 'string') return false;
  if (s.method !== 'accept-all' && s.method !== 'reject-all' && s.method !== 'customize') return false;
  if (typeof s.categories !== 'object' || s.categories === null) return false;
  const cats = s.categories as Record<string, unknown>;
  for (const key of ['necessary', 'functional', 'analytics', 'marketing']) {
    if (cats[key] !== 'granted' && cats[key] !== 'denied') return false;
  }
  return true;
}
